import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RawHtml } from "../../../lib/ReactUtils";
import Suggestion from "../Chat/Suggestions/Suggestion/Suggestion";
import Tag from "../Tags/Tag";
import XIcon from "../../Elements/Icons/XIcon";
import { createUseStyles } from "react-jss";
import { faWindowMinimize } from "@fortawesome/free-solid-svg-icons";
import { makeStylesFromThemeAndCustomStyles } from "../../../lib/StyleFunctions";
import { mergeDeepRight } from "ramda";
import tagStylesObject from "../Tags/BetaTagStyles";

const makeStyles = (theme) =>
  mergeDeepRight(
    {
      headerInterface: {
        zIndex: "5",
        width: "100%",
      },
      innerHeaderInterface: {
        margin: "0",
        padding: "0",
        lineHeight: "1",
        width: "100%",
        boxSizing: "content-box",
        boxShadow: "none",
        height: "42px",
        backgroundColor: theme.chatHeader,
        border: "none",
        display: "flex",
        justifyContent: "space-between",
      },
      title: {
        color: "white",
        fontWeight: "500",
        fontSize: "18px",
        padding: "10px 10px 10px 15px",
        lineHeight: "normal",
        display: "inline-flex",
        margin: "0",
      },
      logo: { marginRight: "8px" },
      ButtonsContainer: {
        marginRight: "15px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      },
      close: {
        cursor: "pointer",
        cssFloat: "right",
        border: "0px none transparent",
        height: "15px",
        fontSize: "20px",
        color: "white",
        backgroundColor: "transparent",
        boxSizing: "border-box",
        marginLeft: "10px",
      },
      minimize: {
        composes: "$close",
        height: "15px",
        width: "18px",
        fontSize: "15px",
      },
      informationButton: {
        composes: "$minimize",
      },
      ribbonContainer: {
        backgroundColor: "white",
        maxHeight: "340px",
        minHeight: "230px",
        transitionProperty: "all",
        transitionDuration: "0.5s",
        transitionTimingFunction: "ease-in-out",
      },
      "@keyframes slide-up": {
        "0%": { opacity: "0", transform: "scaleY(0)" },
        "100%": { opacity: "1", transform: "scaleY(1)" },
      },
      ribbonContainerClose: {
        maxHeight: "0px",
        minHeight: "0px",
        paddingBottom: "0",
      },
      ribbon: {
        color: theme.textColor,
        fontSize: "14px",
        lineHeight: "20px",
        fontWeight: "400",
        padding: "20px 22px 7px 22px",
      },
      ribbonSuggestionContainer: {
        marginLeft: "17px",
        marginBottom: "17px",
      },
    },
    tagStylesObject(theme)
  );

const InfoButton = ({ onClick, styles }) => (
  <button
    onClick={onClick}
    className={styles.informationButton}
    aria-label="Toggle disclaimer message"
    title="Toggle disclaimer message"
  >
    <svg
      width="15"
      height="15"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.5 0C3.80603 0 0 3.80603 0 8.5C0 13.194 3.80603 17 8.5 17C13.194 17 17 13.194 17 8.5C17 3.80603 13.194 0 8.5 0ZM8.5 15.558C4.6029 15.558 1.44196 12.3971 1.44196 8.5C1.44196 4.6029 4.6029 1.44196 8.5 1.44196C12.3971 1.44196 15.558 4.6029 15.558 8.5C15.558 12.3971 12.3971 15.558 8.5 15.558Z"
        fill="white"
      />
      <path
        d="M7.58932 5.16071C7.58932 5.40225 7.68527 5.63389 7.85607 5.80469C8.02686 5.97548 8.2585 6.07143 8.50004 6.07143C8.74158 6.07143 8.97322 5.97548 9.14401 5.80469C9.3148 5.63389 9.41075 5.40225 9.41075 5.16071C9.41075 4.91918 9.3148 4.68753 9.14401 4.51674C8.97322 4.34595 8.74158 4.25 8.50004 4.25C8.2585 4.25 8.02686 4.34595 7.85607 4.51674C7.68527 4.68753 7.58932 4.91918 7.58932 5.16071V5.16071ZM8.9554 7.28571H8.04468C7.9612 7.28571 7.8929 7.35402 7.8929 7.4375V12.5982C7.8929 12.6817 7.9612 12.75 8.04468 12.75H8.9554C9.03888 12.75 9.10718 12.6817 9.10718 12.5982V7.4375C9.10718 7.35402 9.03888 7.28571 8.9554 7.28571Z"
        fill="white"
      />
    </svg>
  </button>
);

const TopRibbon = ({ text, styles, showRibbon, closeRibbon }) => (
  <div
    className={[
      styles.ribbonContainer,
      !showRibbon ? styles.ribbonContainerClose : null,
    ].join(" ")}
  >
    <RawHtml className={styles.ribbon} tag="div">
      {text}
    </RawHtml>
    <div className={styles.ribbonSuggestionContainer}>
      <Suggestion displayText={"Got it"} handleClick={closeRibbon} />
    </div>
  </div>
);

const Header = ({
  title,
  isBeta,
  allowMinimize,
  minimizeWidget,
  closeWidget,
  headerLogo,
  topRibbonText,
  customWidgetStyle,
}) => {
  const [showRibbon, setShowRibbon] = useState(false);
  const useStyles = createUseStyles(
    makeStylesFromThemeAndCustomStyles(makeStyles, customWidgetStyle)
  );
  const styles = useStyles();
  headerLogo =
    customWidgetStyle && customWidgetStyle.headerLogo
      ? customWidgetStyle.headerLogo
      : headerLogo;

  return (
    <>
      <div className={styles.headerInterface}>
        <div className={styles.innerHeaderInterface}>
          <h3 className={styles.title}>
            {headerLogo ? (
              <RawHtml className={styles.logo} tag={"span"}>
                {headerLogo}
              </RawHtml>
            ) : null}
            {title || "Assistant"}
            {isBeta ? <Tag styles={styles} tagText={"BETA"} /> : null}
          </h3>
          <div className={styles.ButtonsContainer}>
            {topRibbonText ? (
              <InfoButton
                onClick={() => setShowRibbon(!showRibbon)}
                styles={styles}
              />
            ) : null}
            {allowMinimize ? (
              <button
                className={styles.minimize}
                onClick={minimizeWidget}
                aria-label="Click to Minimize virtual Assistant assistant"
                title="Click to Minimize virtual Assistant assistant"
              >
                <i className="fas fa-window-minimize" />
                <FontAwesomeIcon aria-hidden="true" icon={faWindowMinimize} />
              </button>
            ) : null}
            <button
              className={styles.close}
              id={Header.MINIMISER_ID}
              onClick={closeWidget}
              aria-label="Click to close virtual Assistant assistant"
              title="Click to close virtual Assistant assistant"
            >
              <XIcon />
            </button>
          </div>
        </div>
      </div>
      {topRibbonText ? (
        <TopRibbon
          text={topRibbonText}
          styles={styles}
          showRibbon={showRibbon}
          closeRibbon={() => {
            setShowRibbon(false);
          }}
        />
      ) : null}
    </>
  );
};

Header.MINIMISER_ID = "airbud-minimizer";

export default Header;
